// ═══════════════════════════════════════════════════════════════
// coverGeometry.js — SINGLE SOURCE OF TRUTH for Lulu cover math.
// ═══════════════════════════════════════════════════════════════
//
// COVER-002 (see docs/DECISION_REGISTER.md and docs/COVER_ARCHITECTURE.md):
// every number that describes the PRINT SHEET GEOMETRY of a Talebound cover
// — bleed, wrap, trim, spine width, the front-panel box, the safe zone —
// lives HERE and nowhere else. `RemnantCoverPDF.js` (the PDF renderer) and
// `coverLayout.js` (per-design content constants + HTML preview constants)
// both import from this module instead of redefining the math.
//
// This module does NOT know anything about a specific cover design's art,
// colors, or copy — that stays in coverLayout.js. It only knows the shape
// of the print sheet, Lulu's spine table, and (for full-wrap "framing"
// covers) the generic math for projecting a point on the source art onto
// the print sheet under `objectFit: 'cover'`.
//
// Lulu 8.5"x11" Casewrap Hardcover cover specs:
//   Spine width: LOOKUP TABLE (not a formula).
//   See: https://help.api.lulu.com/en/support/solutions/articles/64000254616
//
//   Layout (left to right):
//     [0.125" bleed] [0.75" back wrap] [8.5" back cover] [spine] [8.5" front cover] [0.75" front wrap] [0.125" bleed]
//   Height:
//     [0.125" bleed] [0.75" wrap] [11" trim] [0.75" wrap] [0.125" bleed]
//
//   Casewrap hinge: 0.25" groove from spine on both covers (per Lulu docs).
//   Safe zone: 0.5" from trim edge (per Lulu docs).
//   Content is shifted 0.125" toward fore-edge (half the hinge width) so
//   content centers on the optical midpoint of the visible flat board, not
//   the full trim.

// ── Base sheet constants (inches) ──
export const COVER = {
  trimW: 8.5,
  trimH: 11,
  bleed: 0.125,
  wrap: 0.75,
};

// ═══════════════════════════════════════════════════════════════
// SPINE CALCULATION
// ═══════════════════════════════════════════════════════════════

/**
 * Calculate spine width for Lulu hardcover casewrap.
 * Hardcover uses a LOOKUP TABLE (not a formula).
 * Source: https://help.api.lulu.com/en/support/solutions/articles/64000254616
 * Returns width in inches.
 */
export const calculateSpineWidth = (pageCount) => {
  if (pageCount <= 24) return 0.25;
  if (pageCount <= 84) return 0.25;
  if (pageCount <= 140) return 0.5;
  if (pageCount <= 168) return 0.625;
  if (pageCount <= 194) return 0.6875;
  if (pageCount <= 222) return 0.75;
  if (pageCount <= 250) return 0.8125;
  if (pageCount <= 278) return 0.875;
  if (pageCount <= 306) return 0.9375;
  if (pageCount <= 334) return 1.0;
  if (pageCount <= 360) return 1.0625;
  if (pageCount <= 388) return 1.125;
  if (pageCount <= 416) return 1.1875;
  if (pageCount <= 444) return 1.25;
  if (pageCount <= 472) return 1.3125;
  if (pageCount <= 500) return 1.375;
  if (pageCount <= 528) return 1.4375;
  if (pageCount <= 556) return 1.5;
  if (pageCount <= 582) return 1.5625;
  if (pageCount <= 610) return 1.625;
  if (pageCount <= 638) return 1.6875;
  if (pageCount <= 666) return 1.75;
  if (pageCount <= 694) return 1.8125;
  if (pageCount <= 722) return 1.875;
  if (pageCount <= 750) return 1.9375;
  if (pageCount <= 778) return 2.0;
  if (pageCount <= 800) return 2.0625;
  return 2.125;
};

// ═══════════════════════════════════════════════════════════════
// COVER SHEET DIMENSIONS
// ═══════════════════════════════════════════════════════════════
//
// Returns every derived measurement RemnantCoverPDF.js and coverLayout.js
// need, in points (72pt = 1in) unless suffixed `In`. Shape is unchanged from
// the pre-COVER-002 `getCoverDimensions` in RemnantCoverPDF.js — this is a
// behavior-preserving extraction, not a redesign.
export const getCoverDimensions = (pageCount) => {
  const spine = calculateSpineWidth(pageCount);
  const totalW = COVER.bleed + COVER.wrap + COVER.trimW + spine + COVER.trimW + COVER.wrap + COVER.bleed;
  const totalH = COVER.bleed + COVER.wrap + COVER.trimH + COVER.wrap + COVER.bleed;

  return {
    widthIn: totalW,
    heightIn: totalH,
    widthPt: totalW * 72,
    heightPt: totalH * 72,
    spineIn: spine,
    spinePt: spine * 72,
    backStartPt: (COVER.bleed + COVER.wrap) * 72,
    spineStartPt: (COVER.bleed + COVER.wrap + COVER.trimW) * 72,
    frontStartPt: (COVER.bleed + COVER.wrap + COVER.trimW + spine) * 72,
    frontEndPt: (COVER.bleed + COVER.wrap + COVER.trimW + spine + COVER.trimW) * 72,
    safeInset: 0.5 * 72,               // Lulu recommended 0.5" safe zone
    hingeCompensation: 0.125 * 72,     // 0.125" = half the hinge = optical center of the visible flat board
    frontFullLeft: (COVER.bleed + COVER.wrap + COVER.trimW + spine) * 72,
    frontFullTop: (COVER.bleed + COVER.wrap) * 72,
    panelW: COVER.trimW * 72,
    panelH: COVER.trimH * 72,
  };
};

// ═══════════════════════════════════════════════════════════════
// HINGE-COMPENSATED CENTER (charcoal / classic centered designs)
// ═══════════════════════════════════════════════════════════════
//
// The "simple centered" front-cover designs (currently: charcoal) place
// their content in a box with `paddingLeft: safeInset + hingeCompensation,
// paddingRight: safeInset - hingeCompensation` (see FrontFramedCharcoal /
// FrontClassicCharcoal in RemnantCoverPDF.js) so the content's visual
// center sits `hingeCompensation` toward the fore-edge of the panel's bare
// geometric center — the intended optical center of the visible flat board
// once the casewrap hinge groove is accounted for (see the module header).
// This is that resolved center point, algebraically derived from the same
// padding values the component actually uses (not a separate guess): a
// content box spanning [frontStartPt+paddingLeft, frontEndPt-paddingRight]
// has its own center at frontStartPt + paddingLeft + (panelW - paddingLeft
// - paddingRight)/2, which simplifies to frontStartPt + panelW/2 +
// hingeCompensation. Vertical padding is symmetric (safeInset both top and
// bottom), so the vertical center is just the panel's geometric middle.
export function hingeCompensatedCenter(dim) {
  return {
    xPt: dim.frontStartPt + dim.panelW / 2 + dim.hingeCompensation,
    yPt: dim.frontFullTop + dim.panelH / 2,
  };
}

// ═══════════════════════════════════════════════════════════════
// MUSTARD PLUM FULL-WRAP PLACEMENT (spine-aware, wreath-axis-centered)
// ═══════════════════════════════════════════════════════════════
//
// The Mustard Plum design paints one flat piece of art across the ENTIRE
// print sheet (back + spine + front). fullWrapPlacement() is the SINGLE
// SOURCE OF TRUTH for how that art is laid onto the sheet, consumed by BOTH
// RemnantCoverPDF (the PDF <Image>) and the Book Builder HTML preview, so the
// two are identical by construction (COVER-001).
//
// F-COVER-CROP (2026-07-12) — model change: the art used to be stretched
// edge-to-edge with `objectFit: 'cover'` (match-width). Because the sheet's
// aspect changes with spine width but the art's is fixed, the FIXED wreath
// medallion drifted off the front-panel horizontal center as pageCount
// changed — measured +0.10in RIGHT at the common 24-84pp book, ~0 only near
// 168pp, and increasingly LEFT on thick books. This ports render_cover.py's
// model instead: ACTIVELY anchor the wreath's axis of symmetry
// (clearingCentroidPx.x) to the front-panel center at EVERY page count, then
// take the SMALLEST uniform scale that still fully covers the sheet after that
// horizontal anchor (so there is never a background-color gap at an edge).
// Vertical placement stays centered — the art aspect (6650/4463 = 1.4899) is
// so close to the sheet aspect (~1.49) that the extra vertical crop from the
// small scale-up is a fraction of an inch of outer botanical, never the
// medallion. The back/spine art is a continuous botanical "wallpaper" (the
// spine TEXT is placed independently by code), so the sub-half-inch horizontal
// re-anchor is imperceptible there — verified per page count by the full-wrap
// flats in the F-COVER-CROP evidence pack.
//
// NOTE — mustardplum only. floralwrap/burgwrap still use plain
// `objectFit: 'cover'` in RemnantCoverPDF and are intentionally NOT
// re-centered (out of scope; their older art was launch-accepted as-is). The
// projection helpers below are, and always were, called only for mustardplum.
export function fullWrapPlacement(dim) {
  const imgW = MUSTARD_PLUM_SOURCE.imageWidthPx;
  const imgH = MUSTARD_PLUM_SOURCE.imageHeightPx;
  const wreathX = MUSTARD_PLUM_SOURCE.clearingCentroidPx.x;
  const panelCenterX = dim.frontFullLeft + dim.panelW / 2;
  // Smallest uniform scale that keeps the whole sheet covered once the wreath
  // is anchored at panelCenterX:
  //   left  = panelCenterX - wreathX*s <= 0          -> s >= panelCenterX / wreathX
  //   right = left + imgW*s >= sheetW                -> s >= (sheetW - panelCenterX) / (imgW - wreathX)
  //   height: imgH*s >= sheetH                       -> s >= sheetH / imgH
  const sLeft = panelCenterX / wreathX;
  const sRight = (dim.widthPt - panelCenterX) / (imgW - wreathX);
  const sHeight = dim.heightPt / imgH;
  const scale = Math.max(sLeft, sRight, sHeight);
  const leftPt = panelCenterX - wreathX * scale;    // <= 0 by construction (sLeft is in the max)
  const topPt = -(imgH * scale - dim.heightPt) / 2; // vertical center
  return { scale, leftPt, topPt, widthPt: imgW * scale, heightPt: imgH * scale };
}

// Tripwire: the projection helpers below are defined against the Mustard Plum
// source. If a caller ever passes a different image size, fail loudly rather
// than return a silently-wrong fraction/pixel.
function assertMustardPlumDims(imageWidthPx, imageHeightPx, who) {
  if (imageWidthPx !== MUSTARD_PLUM_SOURCE.imageWidthPx || imageHeightPx !== MUSTARD_PLUM_SOURCE.imageHeightPx) {
    throw new Error(
      `${who}: defined for the Mustard Plum source ` +
      `(${MUSTARD_PLUM_SOURCE.imageWidthPx}x${MUSTARD_PLUM_SOURCE.imageHeightPx}); got ${imageWidthPx}x${imageHeightPx}.`
    );
  }
}

// Given a point in the SOURCE ART's own pixel space (e.g. the wreath-opening
// centroid measured by scripts/deriveCoverCenters.mjs), returns the fraction
// of the FRONT PANEL (x,y in [0,1] of panelW/panelH) at which that point
// renders for the sheet `dim`, THROUGH fullWrapPlacement (the same transform
// the art itself goes through). By construction the wreath's own axis returns
// xFraction == 0.5 at every page count.
export function fullWrapAnchorFraction(dim, imageWidthPx, imageHeightPx, anchorPxX, anchorPxY) {
  assertMustardPlumDims(imageWidthPx, imageHeightPx, 'fullWrapAnchorFraction');
  const p = fullWrapPlacement(dim);
  const screenXPt = p.leftPt + anchorPxX * p.scale;
  const screenYPt = p.topPt + anchorPxY * p.scale;
  return {
    xFraction: (screenXPt - dim.frontFullLeft) / dim.panelW,
    yFraction: (screenYPt - dim.frontFullTop) / dim.panelH,
  };
}

// Exact inverse of fullWrapAnchorFraction: given a point in ABSOLUTE PDF
// page space (origin bottom-left, y-up — i.e. exactly what pdfjs-dist
// operator-list/text-content extraction returns), returns the
// corresponding pixel coordinate on the SOURCE ART. Used by the
// coverCentering/coverContent gate to project a rendered text run's bbox
// back onto the art and ask "is this actually sitting on clean background,
// or on painted wreath ink?" (scripts/sampleArtBackground.mjs) — the safe
// boxes elsewhere in this file establish that particular ELEMENT SIZES fit
// the opening by construction, but can't by themselves catch a
// longer/shorter runtime string (e.g. a longer title) shifting text outside
// that assumption; this makes the check a real pixel measurement instead.
export function fullWrapInversePixel(dim, imageWidthPx, imageHeightPx, pdfX, pdfY) {
  assertMustardPlumDims(imageWidthPx, imageHeightPx, 'fullWrapInversePixel');
  const p = fullWrapPlacement(dim);
  const screenXPt = pdfX;                 // absolute sheet X (left-origin) == pdf X
  const screenYPt = dim.heightPt - pdfY;  // pdf Y is bottom-up -> top-down sheet Y
  return {
    px: (screenXPt - p.leftPt) / p.scale,
    py: (screenYPt - p.topPt) / p.scale,
  };
}

// ═══════════════════════════════════════════════════════════════
// MUSTARD PLUM — script-derived opening geometry
// ═══════════════════════════════════════════════════════════════
//
// SCRIPT-DERIVED, NOT HAND-TYPED. Produced by running:
//   node scripts/deriveCoverCenters.mjs --design mustardplum
// (which shells to scripts/derive_cover_centers.py — see that file for the
// full method: enclosed-hole detection via morphological closing +
// binary_fill_holes, NOT color thresholding alone, because the clearing and
// the outer background are the identical mustard color).
//
// ART REVISION 2026-07-08b: Eliane rescaled the wreath ("pulled it in" so
// nothing bleeds off the trim; the opening is larger and more symmetric).
// public/covers/floral_final_fullwrap.jpg was overwritten with the new art
// (same 6650x4463 canvas) and the values below were re-derived against it —
// they SUPERSEDE the original 2026-07-08 measurement (centroid was
// (4911.97, 2155.75), mirror score 0.9936) which in turn superseded the
// original hand-measured CENTER_X=4913/CLEAR_CY=2177 guess. Every revision
// of this art gets its own fresh script run — never hand-adjust these
// numbers to "nudge" something.
//
// Measured against the current public/covers/floral_final_fullwrap.jpg
// (6650x4463px):
//   clearing centroid:      (4893.64, 2171.48) px
//   mirror symmetry score:  0.9956  (1.0 = perfect vertical axis of symmetry)
//   safe inner box (free aspect, half-extents):     498.86w x 997.72h px
//   safe inner box (landscape photo aspect 1.3846): 618.86w x 446.96h px
//   safe inner box (portrait photo aspect 0.7143):  498.86w x 698.41h px
//   safe inner box (text-line aspect 20):           850.86w x  42.54h px
export const MUSTARD_PLUM_SOURCE = {
  fullWrapImageUrl: '/covers/floral_final_fullwrap.jpg',
  imageWidthPx: 6650,
  imageHeightPx: 4463,
  clearingCentroidPx: { x: 4893.638132909529, y: 2171.47821708613 },
  clearingBBoxPx: { left: 3890, top: 848, right: 5897, bottom: 3481 },
  mirrorSymmetryScore: 0.9956478419297063,
  // Half-extents (px, at the art's native resolution) of the largest
  // axis-aligned rectangle centered on the centroid that stays fully
  // inside the enclosed opening, for the photo-frame aspect ratios
  // FLORAL_WRAP.photoFrames uses (see coverLayout.js), the free-aspect
  // maximum, and a thin "text line" band (aspect 20 — wide/short, like an
  // actual line of title/subtitle/author text) used to size
  // clearingMaxWidthPt. Used to derive content sizes that provably fit
  // inside the opening, and to sanity-check them before they ship.
  safeInnerBoxPx: {
    freeAspect: { halfWidth: 498.861867090032, halfHeight: 997.723734180064, aspect: 0.5 },
    landscape: { halfWidth: 618.8618670894687, halfHeight: 446.955917052371, aspect: 1.384615 },
    portrait: { halfWidth: 498.86186709030454, halfHeight: 698.4063345638925, aspect: 0.714286 },
    textLine: { halfWidth: 850.8618670644245, halfHeight: 42.543093353221224, aspect: 20 },
  },
};

// ═══════════════════════════════════════════════════════════════
// MUSTARD PLUM — safe-box -> point-space content sizing
// ═══════════════════════════════════════════════════════════════
//
// Converts a measured safeInnerBoxPx entry (art pixels) into PDF points,
// so a photo frame / text max-width can be sized directly from what the
// derive script measured — never eyeballed, never a separately-guessed
// constant. Uses the REFERENCE-SPINE scale (pageCount 24, the common
// 24-84pp bucket — the same scale factor `mustardPlumOpeningFraction`
// resolves to for that bucket, and the one at which "1 art pixel" maps
// most directly onto "1 printed pixel" for the common case). `marginFactor`
// (default 0.92, an 8% margin) keeps content from visually kissing the
// wreath branches even though the box itself is mathematically the
// largest that still fits — a small, explicit, documented safety margin,
// not a second hidden guess.
export function mustardPlumSafeBoxPt(safeBoxPx, marginFactor = 0.92) {
  const refDim = getCoverDimensions(24);
  const scale = refDim.widthPt / MUSTARD_PLUM_SOURCE.imageWidthPx;
  return {
    widthPt: safeBoxPx.halfWidth * 2 * scale * marginFactor,
    heightPt: safeBoxPx.halfHeight * 2 * scale * marginFactor,
  };
}

/**
 * Safe max width (PDF points) for a single centered line of Mustard Plum
 * title/subtitle/author text, derived from the measured thin "text line"
 * safe box (coverLayout.js's clearingMaxWidthPt). Text still uses the
 * wreath-clearance safe box (unlike the photo below) — text touching a
 * branch reads as a mistake, a photo touching the wreath reads as full-bleed
 * intentional framing, which is why the two use different philosophies.
 */
export function mustardPlumTextMaxWidthPt() {
  return mustardPlumSafeBoxPt(MUSTARD_PLUM_SOURCE.safeInnerBoxPx.textLine).widthPt;
}

// ═══════════════════════════════════════════════════════════════
// MUSTARD PLUM — photo size (generous fill, NOT wreath-safe-constrained)
// ═══════════════════════════════════════════════════════════════
//
// CORRECTION (design/persona loop, 2026-07-08c): the photo was originally
// sized to fit INSIDE the measured wreath-clearance safe box above (the
// same conservative approach text still uses) — reviewed and rejected as
// too small. The photo is meant to FILL the opening generously; touching
// or slightly overlapping the wreath is fine and preferred (unlike text,
// which must stay clear — see mustardPlumTextMaxWidthPt above).
//
// Landscape width is the design/persona loop's OWN approved value
// (candidates tested: 254 / 320 / 380 / 430pt wide — 380 won unanimously;
// 254 was judged too small, 430 crowded the title). This is therefore the
// one number in this file that is a human design decision, not a script
// output — recorded here, cited, so it is not re-guessed later.
export const MUSTARD_PLUM_PHOTO_LANDSCAPE_PT = { widthPt: 380, heightPt: 274.4 }; // preserves the ~1.3847 landscape aspect

/**
 * Portrait photo size: NOT design/persona-loop-approved yet (landscape was
 * reviewed first) — but no longer a geometric estimate either. COVER-002c
 * history (two corrections in sequence):
 *
 * 1. An earlier version derived portrait HEIGHT from a font-metric
 *    RESERVATION ESTIMATE (title/subtitle/author line heights subtracted
 *    from the clearing's bounding-box height). Wrong in practice — a real
 *    render with the title "Dad's Stories" (and even the shorter "My
 *    Life") put the title ON TOP of the honeysuckle florals above the
 *    opening and the author line on top of the flower medallion below it;
 *    the clearing narrows near its top/bottom in a way a bounding-box
 *    estimate can't see. Fixed by direct measurement instead of
 *    estimation: render the actual PDF at a candidate HEIGHT (holding
 *    width proportional) for three representative titles (short, the
 *    reported failing one, and long), project each rendered title/
 *    subtitle/author text run's bbox back onto the source art
 *    (fullWrapInversePixel), and sample the real pixels underneath
 *    (scripts/sampleArtBackground.mjs) for the background fraction:
 *      294.8 x 412.7pt (the old estimate) -> 0.14-0.58 (badly overlapping)
 *      260 x 364pt -> 0.71-0.87 (still failing)
 *      230 x 322pt -> 0.93-0.96 (still failing, close)
 *      215 x 301pt -> 0.983-1.000 (passes with real margin) <- height kept
 *
 * 2. HEIGHT (301pt) is the load-bearing number for text clearance — title is
 *    bottom-anchored at `matOuterH/2 + gap` above the opening centroid and
 *    subtitle/author are top-anchored the same distance below it (see
 *    FrontFramedFloralWrap in RemnantCoverPDF.js), so only the mat's HEIGHT
 *    moves those anchors; the mat's WIDTH has zero effect on title/
 *    subtitle/author vertical position. That means width can grow
 *    independently without reopening the text-overlap bug — done here:
 *    215pt read as too narrow/timid once the "photo may overlap the
 *    wreath, only text must stay clear" rule was confirmed, so width is
 *    widened to 280pt (bolder, fills more of the opening horizontally,
 *    overlapping the side branches — which is fine for a photo) while
 *    height stays at the measured-safe 301pt. Re-verified after widening:
 *    text backgroundFraction unchanged (still 0.983-1.000) because height
 *    didn't change.
 *
 * Recorded here with the measurement method cited so a future art revision
 * re-runs the same process (scripts/sampleArtBackground.mjs against a few
 * representative titles) rather than re-guessing a number.
 * coverCentering.test.js's text-vs-art check (COVER-002c) gates this
 * permanently: it fails the build if any title/subtitle/author text run
 * ever again lands on painted wreath ink, at any title length. The PHOTO
 * itself is deliberately exempt from that check — it is allowed, and
 * expected, to touch or overlap the wreath.
 */
// eslint-disable-next-line no-unused-vars -- matTotalPt kept in the signature
// for API symmetry with the landscape sizing path; this value is measured,
// not computed from it (see comment above).
export function mustardPlumPhotoFramePortraitPt(matTotalPt) {
  return { widthPt: 280, heightPt: 301 };
}

/**
 * Mustard Plum's true opening center, as a fraction of the FRONT PANEL
 * (612x792pt / 8.5x11in), for the sheet described by `dim`
 * (= getCoverDimensions(pageCount)). Spine-aware: recomputes the projection
 * for the actual spine width in `dim` instead of assuming the reference
 * spine (0.25in, pageCount 24-84) applies everywhere.
 */
export function mustardPlumOpeningFraction(dim) {
  return fullWrapAnchorFraction(
    dim,
    MUSTARD_PLUM_SOURCE.imageWidthPx,
    MUSTARD_PLUM_SOURCE.imageHeightPx,
    MUSTARD_PLUM_SOURCE.clearingCentroidPx.x,
    MUSTARD_PLUM_SOURCE.clearingCentroidPx.y
  );
}

// Reference-spine (0.25in, pageCount 24-84 — the common case) fraction,
// precomputed for convenience/documentation. Equal to
// mustardPlumOpeningFraction(getCoverDimensions(24)). At larger spine
// widths the true fraction drifts slightly toward the spine (panel
// visually "opens up" as more of the art's width gets allocated to a wider
// sheet) — see docs/COVER_ARCHITECTURE.md "Spine drift" for the measured
// drift table.
export const MUSTARD_PLUM_OPENING_REFERENCE = mustardPlumOpeningFraction(getCoverDimensions(24));
